import React, { useState } from 'react';

const TrainingDocs = () => {
  const [activeModule, setActiveModule] = useState('intro');
  const [completedModules, setCompletedModules] = useState([]);

  const markModuleComplete = (moduleId) => {
    if (!completedModules.includes(moduleId)) {
      setCompletedModules([...completedModules, moduleId]);
    }
  };

  const trainingModules = [
    {
      id: 'intro',
      title: 'Introduction to Companion Care',
      duration: '15 mins',
      description: 'Understanding the role of a companion driver and our service values'
    },
    {
      id: 'safety',
      title: 'Safety Protocols',
      duration: '20 mins',
      description: 'Essential safety procedures for passenger and driver protection'
    },
    {
      id: 'assistance',
      title: 'Passenger Assistance Techniques',
      duration: '25 mins',
      description: 'Proper methods for helping elderly and disabled passengers'
    },
    {
      id: 'communication',
      title: 'Communication & Customer Service',
      duration: '15 mins',
      description: 'Effective communication with passengers and their families'
    },
    {
      id: 'emergency',
      title: 'Emergency Procedures',
      duration: '20 mins',
      description: 'Handling medical emergencies and emergency contacts'
    },
    {
      id: 'app',
      title: 'Using the Driver App',
      duration: '10 mins',
      description: 'Complete guide to the CompanionCab driver application'
    }
  ];

  const renderModuleContent = () => {
    switch (activeModule) {
      case 'intro':
        return (
          <div className="module-content">
            <h2>Introduction to Companion Care</h2>
            
            <div className="content-section">
              <h3>🎯 Our Mission</h3>
              <p>CompanionCab provides safe, reliable, and compassionate transportation for elderly and disabled individuals who need assistance beyond standard taxi services.</p>
            </div>

            <div className="content-section">
              <h3>👨‍⚕️ Role of a Companion Driver</h3>
              <ul>
                <li><strong>Safe Transportation:</strong> Provide secure door-to-door service</li>
                <li><strong>Physical Assistance:</strong> Help with mobility, getting in/out of vehicle</li>
                <li><strong>Emotional Support:</strong> Offer friendly, patient companionship</li>
                <li><strong>Medical Support:</strong> Assist with medical appointments and medication reminders</li>
                <li><strong>Shopping Assistance:</strong> Help with grocery shopping and errands</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>💝 Core Values</h3>
              <div className="values-grid">
                <div className="value-item">
                  <h4>Compassion</h4>
                  <p>Treat every passenger with kindness and understanding</p>
                </div>
                <div className="value-item">
                  <h4>Dignity</h4>
                  <p>Respect passenger independence and personal choices</p>
                </div>
                <div className="value-item">
                  <h4>Reliability</h4>
                  <p>Always be punctual and dependable</p>
                </div>
                <div className="value-item">
                  <h4>Safety</h4>
                  <p>Prioritize passenger and driver safety above all</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>📋 Service Standards</h3>
              <ul>
                <li>Arrive 5 minutes early for all appointments</li>
                <li>Dress professionally and maintain good hygiene</li>
                <li>Keep vehicle clean and well-maintained</li>
                <li>Communicate clearly and patiently</li>
                <li>Respect passenger privacy and confidentiality</li>
                <li>Follow all traffic laws and safety protocols</li>
              </ul>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('intro')}
            >
              Mark as Complete
            </button>
          </div>
        );

      case 'safety':
        return (
          <div className="module-content">
            <h2>Safety Protocols</h2>
            
            <div className="content-section">
              <h3>🚗 Vehicle Safety</h3>
              <ul>
                <li><strong>Pre-trip Inspection:</strong> Check brakes, lights, tires, and mirrors</li>
                <li><strong>Clean Environment:</strong> Sanitize vehicle between passengers</li>
                <li><strong>Safety Equipment:</strong> First aid kit, emergency contact list</li>
                <li><strong>Accessibility:</strong> Ensure easy entry/exit for mobility aids</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>👤 Passenger Safety</h3>
              <ul>
                <li><strong>Seatbelt Assistance:</strong> Always ensure passengers are properly secured</li>
                <li><strong>Transfer Safety:</strong> Use proper techniques to assist passengers</li>
                <li><strong>Mobility Aids:</strong> Secure walkers and canes properly</li>
                <li><strong>Medical Alerts:</strong> Be aware of passenger medical conditions</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>🚨 Emergency Procedures</h3>
              <div className="emergency-grid">
                <div className="emergency-item">
                  <h4>Medical Emergency</h4>
                  <ol>
                    <li>Call 10177 (ambulance)</li>
                    <li>Contact passenger's emergency contact</li>
                    <li>Stay with passenger until help arrives</li>
                    <li>Report to CompanionCab immediately</li>
                  </ol>
                </div>
                <div className="emergency-item">
                  <h4>Vehicle Breakdown</h4>
                  <ol>
                    <li>Move to safe location if possible</li>
                    <li>Call CompanionCab dispatch</li>
                    <li>Arrange alternative transport</li>
                    <li>Stay with passenger until resolved</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>📱 Emergency Contacts</h3>
              <div className="contact-grid">
                <div className="contact-item">
                  <strong>Emergency Services:</strong> 10177
                </div>
                <div className="contact-item">
                  <strong>CompanionCab Support:</strong> 067 018 7407
                </div>
                <div className="contact-item">
                  <strong>Email:</strong> infocompcab@gmail.com
                </div>
              </div>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('safety')}
            >
              Mark as Complete
            </button>
          </div>
        );

      case 'assistance':
        return (
          <div className="module-content">
            <h2>Passenger Assistance Techniques</h2>
            
            <div className="content-section">
              <h3>🚶‍♂️ Mobility Assistance</h3>
              <div className="technique-grid">
                <div className="technique-item">
                  <h4>Getting In/Out of Vehicle</h4>
                  <ul>
                    <li>Open door fully and secure it</li>
                    <li>Offer your arm for support (don't grab theirs)</li>
                    <li>Allow passenger to set the pace</li>
                    <li>Position seat for easy access</li>
                    <li>Assist with seatbelt if needed</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>🏥 Medical Appointment Support</h3>
              <ul>
                <li><strong>Appointment Reminders:</strong> Confirm details before trip</li>
                <li><strong>Documentation:</strong> Help carry medical files if requested</li>
                <li><strong>Waiting Time:</strong> Stay during appointment if needed</li>
                <li><strong>Medication:</strong> Remind about bringing medications</li>
                <li><strong>Follow-up:</strong> Ensure safe return home</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>🛒 Shopping Assistance</h3>
              <ul>
                <li><strong>Shopping List:</strong> Help organize and read lists</li>
                <li><strong>Physical Support:</strong> Assist with reaching items</li>
                <li><strong>Carrying:</strong> Help transport heavy or multiple bags</li>
                <li><strong>Payment:</strong> Assist at checkout if requested</li>
                <li><strong>Home Delivery:</strong> Help carry items to door</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>❌ Important Don'ts</h3>
              <div className="donts-grid">
                <div className="dont-item">❌ Don't lift without asking</div>
                <div className="dont-item">❌ Don't rush the passenger</div>
                <div className="dont-item">❌ Don't make assumptions about abilities</div>
                <div className="dont-item">❌ Don't handle personal items without permission</div>
                <div className="dont-item">❌ Don't provide medical advice</div>
                <div className="dont-item">❌ Don't leave passenger unattended in unsafe situations</div>
              </div>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('assistance')}
            >
              Mark as Complete
            </button>
          </div>
        );

      case 'communication':
        return (
          <div className="module-content">
            <h2>Communication & Customer Service</h2>
            
            <div className="content-section">
              <h3>💬 Effective Communication</h3>
              <ul>
                <li><strong>Speak Clearly:</strong> Use simple, clear language</li>
                <li><strong>Listen Actively:</strong> Give full attention to passenger needs</li>
                <li><strong>Be Patient:</strong> Allow extra time for responses</li>
                <li><strong>Show Respect:</strong> Use appropriate titles (Mr./Mrs./Dr.)</li>
                <li><strong>Maintain Eye Contact:</strong> Show engagement and respect</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>👂 Communication Challenges</h3>
              <div className="challenge-grid">
                <div className="challenge-item">
                  <h4>Hearing Difficulties</h4>
                  <ul>
                    <li>Face the passenger when speaking</li>
                    <li>Speak slowly and clearly</li>
                    <li>Use gestures if helpful</li>
                    <li>Write notes if necessary</li>
                  </ul>
                </div>
                <div className="challenge-item">
                  <h4>Speech Difficulties</h4>
                  <ul>
                    <li>Be patient and don't interrupt</li>
                    <li>Ask for clarification if needed</li>
                    <li>Use yes/no questions when possible</li>
                    <li>Allow extra time for conversation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>👨‍👩‍👧‍👦 Family Communication</h3>
              <ul>
                <li><strong>Pickup/Dropoff:</strong> Confirm arrival with family members</li>
                <li><strong>Updates:</strong> Provide status updates for long trips</li>
                <li><strong>Concerns:</strong> Report any passenger concerns to family</li>
                <li><strong>Feedback:</strong> Welcome family feedback and suggestions</li>
                <li><strong>Privacy:</strong> Respect family confidentiality</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>📞 Professional Phone Etiquette</h3>
              <ul>
                <li>Answer calls promptly and professionally</li>
                <li>Identify yourself clearly</li>
                <li>Take detailed messages</li>
                <li>Confirm appointment details</li>
                <li>Return calls within 2 hours</li>
              </ul>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('communication')}
            >
              Mark as Complete
            </button>
          </div>
        );

      case 'emergency':
        return (
          <div className="module-content">
            <h2>Emergency Procedures</h2>
            
            <div className="content-section">
              <h3>🚨 Medical Emergency Response</h3>
              <div className="emergency-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Assess the Situation</h4>
                    <p>Check if passenger is conscious and breathing. Do not move passenger unless they are in immediate danger.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Call Emergency Services</h4>
                    <p>Dial 10177 for ambulance. Provide location, passenger condition, and your contact information.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Contact Family/Emergency Contacts</h4>
                    <p>Call passenger's emergency contact immediately. Use passenger's phone if available.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Notify CompanionCab</h4>
                    <p>Call dispatch to report emergency. They will provide additional support and coordinate with hospital.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>💊 Common Medical Conditions</h3>
              <div className="condition-grid">
                <div className="condition-item">
                  <h4>Diabetes</h4>
                  <p><strong>Signs:</strong> Confusion, sweating, shakiness</p>
                  <p><strong>Action:</strong> Offer glucose tablets if conscious, call 10177 if unconscious</p>
                </div>
                <div className="condition-item">
                  <h4>Heart Conditions</h4>
                  <p><strong>Signs:</strong> Chest pain, shortness of breath, dizziness</p>
                  <p><strong>Action:</strong> Keep passenger calm, assist with medication, call 10177</p>
                </div>
                <div className="condition-item">
                  <h4>Stroke</h4>
                  <p><strong>Signs:</strong> Face drooping, arm weakness, speech difficulty</p>
                  <p><strong>Action:</strong> Note time of symptoms, call 10177 immediately</p>
                </div>
                <div className="condition-item">
                  <h4>Falls</h4>
                  <p><strong>Signs:</strong> Pain, inability to move, confusion</p>
                  <p><strong>Action:</strong> Do not move passenger, check for bleeding, call 10177</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>🩹 First Aid Basics</h3>
              <ul>
                <li><strong>Bleeding:</strong> Apply direct pressure with clean cloth</li>
                <li><strong>Choking:</strong> Encourage coughing, call 10177 if airway blocked</li>
                <li><strong>Burns:</strong> Cool with water, do not apply ice or creams</li>
                <li><strong>Unconsciousness:</strong> Check breathing, place in recovery position if breathing</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>📋 Emergency Information to Collect</h3>
              <div className="info-checklist">
                <div className="checklist-item">✓ Passenger's full name and age</div>
                <div className="checklist-item">✓ Known medical conditions</div>
                <div className="checklist-item">✓ Current medications</div>
                <div className="checklist-item">✓ Emergency contact details</div>
                <div className="checklist-item">✓ Doctor/hospital preferences</div>
                <div className="checklist-item">✓ Medical aid information</div>
              </div>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('emergency')}
            >
              Mark as Complete
            </button>
          </div>
        );

      case 'app':
        return (
          <div className="module-content">
            <h2>Using the Driver App</h2>
            
            <div className="content-section">
              <h3>📱 Getting Started</h3>
              <ul>
                <li><strong>Download:</strong> Download CompanionCab Driver from app store</li>
                <li><strong>Login:</strong> Use your driver credentials provided after training</li>
                <li><strong>Profile:</strong> Complete your driver profile with photo and vehicle details</li>
                <li><strong>Status:</strong> Set yourself as "Online" to receive trip requests</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>🚗 Managing Trips</h3>
              <div className="app-feature-grid">
                <div className="feature-item">
                  <h4>Receiving Requests</h4>
                  <p>You'll receive notifications for new trip assignments. Accept or decline within 60 seconds.</p>
                </div>
                <div className="feature-item">
                  <h4>Trip Details</h4>
                  <p>View passenger info, pickup/dropoff locations, special requirements, and estimated duration.</p>
                </div>
                <div className="feature-item">
                  <h4>Navigation</h4>
                  <p>Use built-in GPS navigation or your preferred navigation app for directions.</p>
                </div>
                <div className="feature-item">
                  <h4>Trip Status</h4>
                  <p>Update status: Arrived, Started Trip, Completed. This keeps passengers and families informed.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>💰 Earnings & Payments</h3>
              <ul>
                <li><strong>Trip Fare:</strong> Automatic calculation based on distance and time</li>
                <li><strong>Tips:</strong> Accept cash tips or add to app payment</li>
                <li><strong>Weekly Payments:</strong> Earnings paid every Friday via bank transfer</li>
                <li><strong>Earnings History:</strong> View detailed breakdown of all trips and payments</li>
              </ul>
            </div>

            <div className="content-section">
              <h3>📊 Performance Tracking</h3>
              <div className="performance-grid">
                <div className="performance-item">
                  <h4>Rating System</h4>
                  <p>Passengers rate your service. Maintain 4.5+ star average for bonuses.</p>
                </div>
                <div className="performance-item">
                  <h4>Completion Rate</h4>
                  <p>Complete 95%+ of accepted trips to maintain good standing.</p>
                </div>
                <div className="performance-item">
                  <h4>Punctuality</h4>
                  <p>Arrive within 5 minutes of scheduled time for 90%+ of trips.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h3>🆘 Support Features</h3>
              <ul>
                <li><strong>Emergency Button:</strong> Instant connection to dispatch and emergency services</li>
                <li><strong>Chat Support:</strong> Real-time help with trip issues</li>
                <li><strong>Phone Support:</strong> 24/7 driver helpline for urgent matters</li>
                <li><strong>FAQ Section:</strong> Answers to common questions and procedures</li>
              </ul>
            </div>

            <button 
              className="complete-module-btn"
              onClick={() => markModuleComplete('app')}
            >
              Mark as Complete
            </button>
          </div>
        );

      default:
        return <div>Select a module to view content</div>;
    }
  };

  return (
    <div className="training-docs">
      <div className="training-header">
        <h2>Driver Training Materials</h2>
        <div className="progress-indicator">
          Progress: {completedModules.length}/{trainingModules.length} modules completed
        </div>
      </div>

      <div className="training-layout">
        <div className="modules-sidebar">
          <h3>Training Modules</h3>
          {trainingModules.map(module => (
            <div 
              key={module.id}
              className={`module-item ${activeModule === module.id ? 'active' : ''} ${completedModules.includes(module.id) ? 'completed' : ''}`}
              onClick={() => setActiveModule(module.id)}
            >
              <div className="module-header">
                <h4>{module.title}</h4>
                <span className="module-duration">{module.duration}</span>
                {completedModules.includes(module.id) && <span className="completion-check">✅</span>}
              </div>
              <p className="module-description">{module.description}</p>
            </div>
          ))}
        </div>

        <div className="module-content-area">
          {renderModuleContent()}
        </div>
      </div>

      <div className="training-footer">
        <div className="certification-info">
          <h3>📜 Certification</h3>
          <p>Complete all modules to receive your CompanionCab Driver Certification</p>
          {completedModules.length === trainingModules.length && (
            <div className="certification-complete">
              <h4>🎉 Congratulations!</h4>
              <p>You've completed all training modules. Your certification will be issued within 24 hours.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingDocs;